import {
  type InteractiveAuthResponse,
  InteractiveAuthResponseEnum,
  purchaseApi,
} from "@b2bportal/purchase-api";
import {
  type CartContext,
  type CartFulfillError,
  CartFulfillErrorModal,
  CartFulfillState,
} from "@checkout/index";
import { getFulfillCipherText } from "@checkout/states/Cart/CartFulfill/selectors";
import { axiosInstance } from "@hopper-b2b/api";
import {
  endPollingPerformance,
  PollingResult,
  PollingType,
  startPollingPerformance,
} from "@hopper-b2b/utilities";

const POLL_INTERVAL = 1000;

export const pollInteractivePaymentAuthService = (
  context: CartContext,
  _event: unknown
) =>
  new Promise<InteractiveAuthResponse | CartFulfillError>((resolve, reject) => {
    const fulFillCipherText = getFulfillCipherText({ context });
    if (fulFillCipherText == null) {
      reject({
        failedAt: CartFulfillState.polling,
        type: CartFulfillErrorModal.NoCipherText,
      });
    }

    const startPerformance = startPollingPerformance();

    // Using the anonymous async function because it is easier to read the combination
    // of awaiting a function call in the while loop than to do `.then.catch`.
    // The reason we don't make the entire function async is because the code must return an error
    // state in a specific type, but TypeScript doesn't like it when we throw a non-Error object.
    // It's better and easier to `reject` it instead of `throw`ing it.
    (async () => {
      // Disagree with eslint here. `while (true)` is a useful idiom.

      while (true) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));

        let res = await purchaseApi(axiosInstance)
          .apiV1PurchaseFulfillPollInteractiveAuthPut(fulFillCipherText)
          .then((res) => res.data);
        if (res["Response"]) {
          res = res["value"];
        }
        if (
          res.InteractiveAuthResponse === InteractiveAuthResponseEnum.Failure
        ) {
          // get out of the anonymous async function. handle the error later below.
          return res;
        }

        if (
          res.InteractiveAuthResponse === InteractiveAuthResponseEnum.Pending
        ) {
          // continue the while loop
          continue;
        }

        // everything else we just get out of the loop and handle the response below
        // get out of the anonymous async function
        return res;
      }
    })()
      .then((res) => {
        switch (res.InteractiveAuthResponse) {
          case InteractiveAuthResponseEnum.Failure: {
            endPollingPerformance(
              startPerformance,
              PollingType.INTERACTIVEPAYMENTAUTH,
              PollingResult.FAILURE
            );
            reject({
              failedAt: CartFulfillState.polling,
              type: CartFulfillErrorModal.FailedPolling,
              data: res.errors,
            });
            break;
          }

          case InteractiveAuthResponseEnum.PendingInteractivePaymentAuth: {
            endPollingPerformance(
              startPerformance,
              PollingType.INTERACTIVEPAYMENTAUTH,
              PollingResult.CHALLENGE
            );

            resolve(res);
            break;
          }

          case InteractiveAuthResponseEnum.NotRequested: {
            endPollingPerformance(
              startPerformance,
              PollingType.INTERACTIVEPAYMENTAUTH,
              PollingResult.SUCCESS
            );
            resolve(res);
            break;
          }
        }
      })
      .catch((e) => {
        endPollingPerformance(
          startPerformance,
          PollingType.INTERACTIVEPAYMENTAUTH,
          PollingResult.FAILURE
        );
        reject({
          failedAt: CartFulfillState.polling,
          type: CartFulfillErrorModal.Unknown,
          data: [e],
        });
      });
  });
